import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { hasPermission, getUserCustomerAccess, getDefaultCustomer } from './UserRoleHelpers';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: string;
  isKonfidencyAdminOnly?: boolean;
  fallback?: React.ReactNode;
}

/**
 * ProtectedRoute component that conditionally renders content based on user permissions
 * 
 * @param children - Content to render if user has permission
 * @param permission - Required permission for access
 * @param isKonfidencyAdminOnly - If true, only Konfidency admins can access
 * @param fallback - Optional content to render if user lacks permission
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  permission, 
  isKonfidencyAdminOnly = false,
  fallback = <div className="alert alert-danger">Acesso negado. Você não tem permissão para acessar esta página.</div>
}) => {
  const { user } = useAuth0();
  
  // Check for Konfidency admin access
  if (isKonfidencyAdminOnly) {
    const access = getUserCustomerAccess(user);
    if (!access.isKonfidencyAdmin) {
      return <>{fallback}</>;
    }
    return <>{children}</>;
  }
  
  // Regular permission check
  if (!permission) {
    return <>{children}</>;
  }
  
  // `getDefaultCustomer` em vez de ler `activeCustomer` cru: no primeiro acesso o
  // storage está vazio e a leitura direta devolvia "", negando toda página com
  // gate de permissão mesmo com token correto. Ele também valida o valor
  // guardado contra o token, então storage apontando para loja à qual o usuário
  // perdeu acesso deixa de produzir o mesmo sintoma.
  const currentCustomer = getDefaultCustomer(user) || "";
  
  // Check if user has the required permission
  const hasAccess = user && currentCustomer && hasPermission(user, currentCustomer, permission);
  
  if (!hasAccess) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};

/**
 * Higher-order component for route protection
 */
export const withPermission = (permission: string, fallback?: React.ReactNode) => {
  return <T extends object>(Component: React.ComponentType<T>) => {
    const ProtectedComponent: React.FC<T> = (props) => (
      <ProtectedRoute permission={permission} fallback={fallback}>
        <Component {...props} />
      </ProtectedRoute>
    );
    
    ProtectedComponent.displayName = `withPermission(${Component.displayName || Component.name})`;
    return ProtectedComponent;
  };
};

/**
 * Component for conditional rendering based on permissions
 */
interface ConditionalRenderProps {
  children: React.ReactNode;
  permission: string;
  customer?: string;
}

export const ConditionalRender: React.FC<ConditionalRenderProps> = ({ 
  children, 
  permission, 
  customer 
}) => {
  const { user } = useAuth0();
  
  const targetCustomer = customer || getDefaultCustomer(user) || "";
  const hasAccess = user && targetCustomer && hasPermission(user, targetCustomer, permission);
  
  return hasAccess ? <>{children}</> : null;
};

export default ProtectedRoute;